import { mapIndiceToDocGroup } from 'utils/commonTools';
import translate from 'utils/translate';

const config = {
  apiUrl: '/api/',

  /* Site settings */
  site: {
    name: 'LAKITUTKA',
    slogan: 'lainvalmistelu läpivalaistuna',
  },

  defaultDocGroup: 'hallituksen_esitykset',

  activeIndices: [
    'lausuntokierros',
    'lakialoite',
    'kansalaisaloite',
    'hallituksen_esitykset',
    'valiokunta_asiakirjat',
    'he_asiantuntijalausunnot',
    'poytakirja',
    // 'eduskunnan_vastaukset', NO INDEX!
  ],

  /*
   * DOCUMENT GROUP SETTINGS
   * ! group's main key is used in API-call urls
   * name: Readable name of the document group
   * searchPrompt: placeholder for the text field in search form
   * facetFields: fields to use in aggregating and filtering search results (same as in backend)
   * infoFields: fields to display in info-area in search results and document page (1-2)
   * tagFields: for tag-type list-fields, like 'asiasanat' (if any)
   * multiSelectFields: fields to include in preFilters
   * searchable: boolean for if group is added to seach page tabs
   */
  docGroups: {
    lausuntokierros: {
      name: 'Valmisteluasiakirjat',
      singular: 'Lainsäädäntöhanke',
      timelineName: 'Hankkeen aloitus',
      searchPrompt: 'Hae valmisteluasiakirjoista',
      facetFields: [
        'asiakirjat.tyyppi',
        'year',
        'tila',
        'asettaja',
        'asiasanat',
        'laatija'
      ],
      infoFields: ['aloitus_paiva', 'hallitus_hk', 'laatimispaiva', 'laatija'],
      tagFields: ['asiasanat'],
      multiSelectFields: ['asettaja', 'tila'],
      radioSelectFields: [
        {
          name: 'Asetukset',
          id: 'asetukset',
          fields: [
            ['without', 'Ilman asetuksia'],
            ['strictly', 'Pelkät asetukset'],
          ],
        },
      ],
      searchable: true,
    },
    lausuntokierros_asiakirjat: {
      name: 'Lausuntokierroksen asiakirjat',
      singular: 'Lausunto',
      infoFields: ['asettaja', 'laatija'],
      searchable: false,
    },
    lausuntokierros_lausunnot: {
      name: 'Lausuntokierroksen lausunnot',
      singular: 'Lausunto',
      infoFields: ['asettaja', 'laatija'],
      searchable: false,
    },
    lausuntokierros_muut: {
      name: 'Lausuntokierroksen muut asiakirjat',
      singular: 'Lausuntokierroksen asiakirja',
      infoFields: ['asettaja', 'laatija'],
      searchable: false,
    },
    lakialoite: {
      name: 'Lakialoitteet',
      singular: 'Lakialoite',
      searchPrompt: 'Hae lakialoitteista',
      facetFields: [
        '_index',
        'year',
        'asian_tila',
        'hallitus_hk',
        'asiasanat',
      ],
      infoFields: ['laadittu', 'hallitus_hk'],
      tagFields: ['asiasanat'],
      multiSelectFields: ['allekirjoittaja', 'hallitus_hk'],
      radioSelectFields: [],
      searchable: true,
      // hideFromProduction: true,
    },
    kansalaisaloite: {
      name: 'Kansalaisaloitteet',
      singular: 'Kansalaisaloite',
      searchPrompt: 'Hae kansalaisaloitteista',
      facetFields: [
        '_index',
        'year',
        'asian_tila',
        'hallitus_hk',
        'asiasanat',
      ],
      infoFields: ['laadittu', 'hallitus_hk'],
      tagFields: ['asiasanat'],
      multiSelectFields: ['allekirjoittaja', 'hallitus_hk'],
      radioSelectFields: [],
      searchable: false,
      // hideFromProduction: true,
    },
    hallituksen_esitykset: {
      name: 'Hallituksen esitykset',
      singular: 'Hallituksen esitys',
      searchPrompt: 'Hae hallituksen esityksistä',
      facetFields: [
        'year',
        'ministerio',
        'hallitus_hk',
        'asiasanat',
      ],
      infoFields: ['laadittu', 'hallitus_hk'],
      secondaryInfoField: ['ministerio'],
      tagFields: ['asiasanat'],
      multiSelectFields: ['ministerio', 'hallitus_hk'],
      radioSelectFields: [
        {
          name: 'Talousarviot',
          id: 'talousarviot',
          fields: [
            ['without', 'Ilman talousarvioita'],
            ['strictly', 'Pelkät talousarviot'],
          ],
        },
        {
          name: 'Eduskunnan kirjelmä',
          id: 'eduskunnan_kirjelma',
          fields: [
            ['without', 'Ilman eduskunnan kirjelmiä'],
            ['strictly', 'Pelkät eduskunnan kirjelmät'],
          ],
        },
      ],
      searchable: true,
      searchView: {
        handlingState: 'asian_tila',
      },
    },
    he_asiantuntijalausunnot: {
      name: 'Asiantuntijalausunnot',
      singular: 'Asiantuntijalausunto',
      searchPrompt:
        'Hae valiokuntien asiantuntijalausunnoista',
      facetFields: ['year', 'type', 'valiokunta'],
      infoFields: ['laadittu', 'valiokunta'],
      multiSelectFields: ['valiokunta', 'type'],
      searchable: false,
    },
    valiokunta_asiakirjat: {
      name: 'Valiokunta-asiakirjat',
      singular: 'Valiokunta-asiakirja',
      searchPrompt: 'Hae valiokunta-asiakirjoista',
      facetFields: ['year', 'type', 'valiokunta', 'hallitus_hk'],
      tagFields: ['asiasanat'],
      infoFields: ['laadittu', 'hallitus_hk'],
      multiSelectFields: ['valiokunta', 'type', 'hallitus_hk'],
      radioSelectFields: [],
      searchable: true,
    },
    poytakirja: {
      name: 'Eduskuntakeskustelut',
      singular: 'Eduskuntakeskustelu',
      searchPrompt: 'Hae eduskuntakeskusteluista',
      facetFields: ['year', 'asiasanat', 'hallitus_hk'],
      infoFields: [],
      multiSelectFields: ['hallitus_hk'],
      tagFields: ['asiasanat'],
      radioSelectFields: [],
      searchable: true,
    },
    eduskunnan_vastaukset: {
      name: 'Eduskunnan vastaukset',
      singular: 'Eduskunnan vastaus',
      infoFields: ['toimija'],
      tagFields: ['asiasanat'],
      searchable: false,
    },
    laws: {
      name: 'Lakipäätökset',
      singular: 'Lakipäätös',
      infoFields: [],
      searchable: false,
    },
    all: {
      name: 'Kaikki asiakirjat',
      singular: 'Asiakirja',
      searchPrompt: 'Hae kaikista asiakirjoista',
      facetFields: ['year', '_index'],
      infoFields: [
        'laadittu',
        'hallitus_hk',
        'valiokunta',
        'aloitus_paiva',
      ],
      tagFields: ['asiasanat'],
      multiSelectFields: [],
      radioSelectFields: [],
      searchable: true,
    },
  },
};

config.docGroups.all.formatTitle = function(
  source,
  index,
  highlighted
) {
  const found = mapIndiceToDocGroup(index)
  if (found) {
    const docGroup = found.replace(
      '-',
      '_',
    );

    if ('formatTitle' in config.docGroups[docGroup]) {
      return `${translate(index)}: ${config.docGroups[
        docGroup
      ].formatTitle(source, null, highlighted)}`;
    }
  }
  return highlighted ? `${translate(index)}: ${highlighted[0]}` : `${translate(index)}: ${source.title}`;
};

config.docGroups.all.formatSecondaryInfoField = function(
  source,
  index,
) {
  const found = mapIndiceToDocGroup(index)
  if (found) {
    const docGroup = found.replace(
      '-',
      '_',
    );

    if (
      'formatSecondaryInfoField' in config.docGroups[docGroup]
    ) {
      return config.docGroups[
        docGroup
      ].formatSecondaryInfoField(source, null);
    }
  }
  return null;
};

config.docGroups.valiokunta_asiakirjat.formatSecondaryInfoField =
  function(source, index) {
    if (
      source.type === 'Asiantuntijalausunto' &&
      source.liittyy
    ) {
      return `<span class="info-field--key">Liittyy:</span> <span class="info-field--value">${source.liittyy}</span>`;
    }
    if (source.hanke) {
      return `<span class="info-field--key">Hanke:</span> <span class="info-field--value">${source.hanke}</span>`;
    }
    return null;
  };

config.docGroups.poytakirja.formatSecondaryInfoField =
  function(source, index) {
    if (source.hanke) {
      return `<span class="info-field--key">Hanke:</span> <span class="info-field--value">${source.hanke}</span>`;
    }
    return null;
  };

config.docGroups.lakialoite.formatSecondaryInfoField =
  function(source, index) {
    if (
      source.allekirjoittaja &&
      source.tunnus.startsWith('LA')
    ) {
      return `<span class="info-field--key">Ensimmäinen allekirjoittaja:</span> <span class="info-field--value">${source.allekirjoittaja}</span>`;
    }
    return null;
  };

config.docGroups.hallituksen_esitykset.formatSecondaryInfoField =
  function(source, index) {
    if (source.ministerio) {
      return `<span class="info-field--key">Valmisteltu</span> <span class="info-field--value">${source.ministerio.toLowerCase()}ssä</span>`;
    }
    return null;
  };

config.docGroups.lausuntokierros.formatSecondaryInfoField =
  function(source, index) {
    if (source.asettaja) {
      return `<span class="info-field--key">Valmisteluvastuussa</span> <span class="info-field--value">${source.asettaja.toLowerCase()}</span>`;
    }
    return null;
  };

config.docGroups.kansalaisaloite.formatTitle = function(
  source,
  index,
  highlighted
) {
  if (source.tunnus && source.tunnus.startsWith('KAA')) {
    return highlighted ? `Kansalaisaloite: ${highlighted[0]}` : `Kansalaisaloite: ${source.title}`;
  }
  return highlighted ? highlighted[0] : source.title;
};

config.docGroups.lakialoite.formatTitle = function(
  source,
  index,
  highlighted
) {
  if (source.tunnus && source.tunnus.startsWith('KAA')) {
    return highlighted ? `Kansalaisaloite: ${highlighted[0]}` : `Kansalaisaloite: ${source.title}`;
  }
  const src = highlighted ? highlighted[0] : source.title
  const title =
    src.charAt(0).toLowerCase() +
    src.slice(1);
  return `Kansanedustajan ${title}`;
};

config.docGroups.poytakirja.formatTitle = function(
  source,
  index,
  highlighted
) {
  const src = highlighted ? highlighted[0] : source.title
  const result = src.match(
    '[0-9]{1,2}([-/.])[0-9]{1,2}[-/.][0-9]{4}',
  );

  if (result) {
    return `Eduskunnan täysistunto ${result[0]}`;
  }
  return highlighted ? highlighted[0] : source.title;
};

config.docGroups.valiokunta_asiakirjat.formatTitle =
  function(source, index, highlighted) {

    const src = highlighted ? highlighted[0] : source.title

    if (source.type === 'Asiantuntijalausunto') {
      const title = src
        .split(/[0-9]{4}/)
        .pop()
        .replace('Asiantuntijalausunto', '')
        .trim();
      return `Asiantuntijalausunto - ${title}`;
    }

    if (source.year < 2015) {
      return src;
    }
    if (!source.valiokunta) {
      return src;
    }
    const valiokunta = source.valiokunta
      .replace(/..$/, 'nan')
      .toLowerCase();
    const vkType = source.type.replace('Valiokunnan ', '');
    const title = src
      .replace(
        'Hallituksen esitys',
        'hallituksen esityksestä',
      )
      .replace('Lakialoite', 'lakialoitteesta')
      .replace('Kansalaisaloite', 'kansalaisaloitteesta');

    return `Eduskunnan ${valiokunta} ${vkType} ${title}`;
  };

export default config;
